import dayjs from "dayjs";

import type {
  LinkedWorkflow,
  LinkedWorkflowsPage,
  WorkflowBuilderKind,
  WorkflowsChangedPayload,
} from "@/types/linkedWorkflows";

export interface LinkedWorkflowsSearchParams {
  locationId: string;
  productType: string;
  assetId: string;
  pageLimit?: number;
}

/**
 * One capped search, no pagination (owner decision, review round 1). 100 is not yet
 * verified against the live API -- confirm in the slice 12 staging check.
 */
export const LINKED_WORKFLOWS_PAGE_LIMIT = 100;

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

/**
 * Same id rule as the spm-ts host bridge
 * (spm-ts src/pmd/pages/marketing/formBuilderWorkflowBridge.ts WORKFLOW_ID_PATTERN) --
 * keep both in sync so the two sides of the bridge narrow ids identically.
 */
export const WORKFLOW_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export function isValidWorkflowId(value: unknown): value is string {
  return typeof value === "string" && WORKFLOW_ID_PATTERN.test(value);
}

/** Anything that is not exactly "advanced" opens the standard builder */
export function toWorkflowBuilderKind(value: unknown): WorkflowBuilderKind {
  return value === "advanced" ? "advanced" : "standard";
}

/** Ported from spm-ts workflow-hotlinks lib/linked-workflows.ts -- keep the filter tree in sync */
export function buildLinkedWorkflowsSearchBody({
  locationId,
  productType,
  assetId,
  pageLimit = LINKED_WORKFLOWS_PAGE_LIMIT,
}: LinkedWorkflowsSearchParams) {
  return {
    locationId,
    pageLimit,
    filters: [
      {
        field: "childNode",
        operator: "has_child",
        value: [
          {
            field: "assets",
            operator: "nested",
            value: [
              {
                group: "AND",
                filters: [
                  { field: "value_array", operator: "contains_set", value: [assetId] },
                  {
                    group: "AND",
                    filters: [{ field: "type", operator: "contains", value: productType }],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };
}

export type LinkedWorkflowsSearchBody = ReturnType<typeof buildLinkedWorkflowsSearchBody>;

function isAdvancedBuilder(meta: unknown): boolean {
  if (!isRecord(meta) || !isRecord(meta.rawData)) return false;
  const rawMeta = meta.rawData.meta;
  if (!isRecord(rawMeta) || !isRecord(rawMeta.advanceCanvasMeta)) return false;
  return rawMeta.advanceCanvasMeta.enabled === true;
}

function toLinkedWorkflow(value: unknown, locationId: string): LinkedWorkflow | null {
  if (!isRecord(value) || value.deleted === true) return null;
  const id = readString(value.id) || readString(value.workflowId);
  const name = readString(value.name);
  // A row from another location, or with an id the host would reject, is never actionable
  if (!isValidWorkflowId(id) || !name || readString(value.locationId) !== locationId) return null;
  const permissionMeta = isRecord(value.permissionMeta) ? value.permissionMeta : {};
  const updatedAt = readString(value.updatedAt);
  return {
    id,
    name,
    locationId,
    status: readString(value.status),
    updatedAt,
    updatedAtLabel: formatLastUpdated(updatedAt),
    canWrite: permissionMeta.canWrite !== false,
    effectiveBuilder: isAdvancedBuilder(value.meta) ? "advanced" : "standard",
  };
}

const EMPTY_PAGE: LinkedWorkflowsPage = { total: 0, truncated: false, workflows: [] };

export function parseLinkedWorkflowsResponse(
  data: unknown,
  { locationId }: { locationId: string }
): LinkedWorkflowsPage {
  if (!isRecord(data) || !Array.isArray(data.workflows)) return { ...EMPTY_PAGE };
  const returned = data.workflows.length;
  const workflows = data.workflows
    .map((item) => toLinkedWorkflow(item, locationId))
    .filter((item): item is LinkedWorkflow => item !== null);
  const total = typeof data.count === "number" && data.count >= 0 ? data.count : returned;
  // Compare with the raw rows returned, so rows dropped by narrowing don't look like a cap
  return { total, truncated: total > returned, workflows };
}

export type WorkflowStatusTagType = "success" | "default" | "info";

export interface WorkflowStatusDisplay {
  /** i18n key for known statuses; null means show `label` as-is */
  labelKey: string | null;
  label: string;
  tagType: WorkflowStatusTagType;
}

const KNOWN_STATUSES: Record<string, Omit<WorkflowStatusDisplay, "label">> = {
  published: { labelKey: "common.workflowStatusPublished", tagType: "success" },
  draft: { labelKey: "common.workflowStatusDraft", tagType: "default" },
};

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

/** Mirrors the workflow team's StatusCell colours */
export function getStatusDisplay(status: string): WorkflowStatusDisplay {
  const trimmed = status.trim();
  const known = KNOWN_STATUSES[trimmed.toLowerCase()];
  return {
    labelKey: known?.labelKey ?? null,
    label: capitalize(trimmed),
    tagType: known?.tagType ?? "info",
  };
}

/** "Last Updated" column: date only, e.g. "Sep 29, 2026" */
export function formatLastUpdated(iso: string): string {
  if (!iso) return "";
  const date = dayjs(iso);
  return date.isValid() ? date.format("MMM D, YYYY") : "";
}

const CHANGED_REASONS: ReadonlyArray<WorkflowsChangedPayload["reason"]> = [
  "hotlinks-closed",
  "builder-closed",
];

/** Host payloads are untrusted -- keep only what the list refresh needs */
export function narrowWorkflowsChanged(payload: unknown): WorkflowsChangedPayload {
  if (!isRecord(payload)) return { reason: "hotlinks-closed" };
  const reason = CHANGED_REASONS.find((item) => item === payload.reason);
  if (!reason) {
    // Still refresh (something changed), but surface host/builder contract drift
    console.warn("[connections-workflows] unknown workflowsChanged reason", {
      reason: typeof payload.reason === "string" ? payload.reason.slice(0, 40) : typeof payload.reason,
    });
    return { reason: "hotlinks-closed" };
  }
  const workflowId = readString(payload.workflowId);
  return isValidWorkflowId(workflowId) ? { reason, workflowId } : { reason };
}

const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1"]);

/**
 * The host app origin from the Postmate handshake, used to build new-tab workflow links.
 * Only a bare https origin (or http on localhost for dev) is accepted.
 */
export function parseAppOrigin(value: unknown): string | null {
  if (typeof value !== "string" || !value) return null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null; // not a URL -- treat as missing so callers fall back to opening inline
  }
  const secure = url.protocol === "https:";
  const localDev = url.protocol === "http:" && LOCAL_HOSTNAMES.has(url.hostname);
  if (!secure && !localDev) return null;
  if (url.username || url.password || url.search || url.hash) return null;
  if (url.pathname !== "/" && url.pathname !== "") return null;
  return url.origin;
}

/** Same path as the workflow team's getWorkflowUrl (spm-ts workflow-hotlinks lib/navigation.ts) */
export function buildWorkflowUrl(
  origin: string,
  locationId: string,
  workflowId: string,
  effectiveBuilder: WorkflowBuilderKind
): string {
  const path = `/location/${encodeURIComponent(locationId)}/workflow/${encodeURIComponent(workflowId)}`;
  return `${origin}${path}${effectiveBuilder === "advanced" ? "/advanced-canvas" : ""}`;
}
